import React, {useRef, useState} from 'react'
import {Box, Flex} from '@chakra-ui/react'
import {Icon, Picture} from '_uiComponents'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Navigation, Scrollbar} from 'swiper/modules'
import type SwiperType from 'swiper'
import {useIntl} from 'react-intl'
import {useAnalytics} from '_customHooks'

interface ProductImage {
    src: string
    alt: string
}

export interface ProductImagesCarouselProps {
    productImages: Array<ProductImage>
    isDesktop?: boolean
    hasLazy?: boolean
    productId: string
    analyticsProductId: string
    analyticsList: string | undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    styles: Record<string, any>
    showHover: boolean
}

/**
 * @name ProductImagesCarousel
 *
 * @description
 * Displays a product tile images carousel.
 *
 * @remark
 * The component displays a carousel of all product images.
 * It also supports lazy loading.
 *
 * @param {ProductImagesCarouselProps} props - The props for the component.
 * @param {Array<ProductImage>} props.productImages - The product images.
 * @param {boolean} props.isDesktop - Boolean indicating if the component is being rendered on a desktop.
 * @param {boolean} props.hasLazy - Boolean indicating if the tile images should be lazily loaded.
 * @param {string} props.productId - The product ID.
 * @param {Record<string, any>} props.styles - Custom styles for the component.
 *
 * @returns Rendered `ProductImage` component.
 */
export const ProductImagesCarousel: React.FC<ProductImagesCarouselProps> = ({
    productImages,
    isDesktop = false,
    hasLazy = false,
    productId,
    analyticsProductId,
    analyticsList,
    styles,
    showHover = true,
}) => {
    const {formatMessage} = useIntl()
    const {analyticsEventTileImageCarouselSlideChange} = useAnalytics()

    const swiperRef = useRef<{swiper: SwiperType}>(null)
    const fadeImageRef = useRef<HTMLElement | null>(null)
    const isHovering = useRef<boolean>(false)

    const [renderedIndexes, setRenderedIndexes] = useState<Set<number>>(() => new Set([0, 1]))
    const currentRealIndex = swiperRef.current?.swiper?.realIndex ?? 0
    const showArrows = currentRealIndex !== 0

    const ensureCurrentAndNext = (realIndex: number) => {
        setRenderedIndexes((prev) => {
            const next = new Set(prev)
            const current = realIndex
            const nextIndex = (realIndex + 1) % productImages.length

            next.add(current)
            next.add(nextIndex)

            return next
        })
    }

    const handleSlideChange = (swiper: SwiperType): void => {
        const currIndex = swiper.realIndex

        if (isDesktop && currIndex === 0 && isHovering.current) {
            fadeImageRef.current?.classList.add('hidden', 'invisible')
        }
    }

    const handleAfterSlideChange = (swiper: SwiperType): void => {
        const currIndex = swiper.realIndex

        if (currIndex === 0 && isHovering.current) {
            fadeImageRef.current?.classList.remove('hidden')
        } else if (currIndex === 1 && isHovering.current) {
            fadeImageRef.current?.classList.remove('hidden', 'invisible')
        }

        ensureCurrentAndNext(currIndex)

        if (!isDesktop) {
            requestAnimationFrame(() => {
                triggerAnalyticsSlideChange()
            })
        }
    }

    const handleMouseEnter = () => {
        const swiper = swiperRef?.current?.swiper
        isHovering.current = true

        // when hovering on first slide -> swipe to second slide (hover image).
        if (swiper && swiper.realIndex === 0) {
            fadeImageRef.current?.classList.remove('invisible')

            setTimeout(() => {
                swiper.slideNext(0)

                if (!isHovering.current && swiper.realIndex === 1) {
                    handleMouseLeave()
                }
            }, 250)
        }
    }

    const handleMouseLeave = () => {
        const swiper = swiperRef?.current?.swiper
        isHovering.current = false

        // when stopping hover on second slide (hover image) -> swipe back to first slide.
        if (swiper && swiper.realIndex === 1) {
            swiper.slidePrev(0)
            fadeImageRef.current?.classList.add('invisible')
        }
    }

    const handleArrowsClick = (event: React.MouseEvent) => {
        // prevent productClick event from being triggered
        event.stopPropagation()

        triggerAnalyticsSlideChange()
    }

    const triggerAnalyticsSlideChange = () => {
        analyticsEventTileImageCarouselSlideChange({
            productId: analyticsProductId,
            list: analyticsList,
        })
    }

    return (
        <Box sx={styles.carouselSlideContainer} onMouseEnter={showHover ? handleMouseEnter : undefined} onMouseLeave={showHover ? handleMouseLeave : undefined}>
            {isDesktop && (
                <Flex
                    as="button"
                    sx={{...styles.navigationPrev, visibility: showArrows ? undefined : 'hidden'}}
                    aria-label={formatMessage({id: 'accessibility.button.slide.previous.photo', defaultMessage: 'Previous message'})}
                    className={`swiper-button-prev swiper-button-prev-product-tile--${productId}`}
                    data-testid="swiper-button-prev-product-tile"
                    onClick={(event) => handleArrowsClick(event)}
                >
                    <Icon name="IconChevronLeft" size="lg" />
                </Flex>
            )}
            <Swiper
                className="productTile__imagesCarousel"
                modules={[Navigation, Scrollbar]}
                scrollbar={{
                    hide: false,
                }}
                navigation={{
                    prevEl: `.swiper-button-prev-product-tile--${productId}`,
                    nextEl: `.swiper-button-next-product-tile--${productId}`,
                }}
                effect="slide"
                slidesPerView={1}
                loop={true}
                allowTouchMove={!isDesktop}
                updateOnWindowResize={true}
                ref={swiperRef}
                onSwiper={(swiper) => {
                    if (swiperRef.current) {
                        swiper.update()
                    }

                    if (isDesktop) {
                        fadeImageRef.current = swiper.el.querySelector('.productTile__image-hover-fade')
                    }

                    ensureCurrentAndNext(swiper.realIndex)
                }}
                onRealIndexChange={(swiper) => {
                    handleSlideChange(swiper)
                }}
                onSlideChangeTransitionEnd={(swiper) => handleAfterSlideChange(swiper)}
            >
                {productImages.map((image, index) => {
                    const shouldRender = renderedIndexes.has(index)

                    return (
                        <SwiperSlide key={`${image.src}-${productId}-${index}`}>
                            {shouldRender && (
                                <>
                                    {index === 0 && productImages.length > 1 && isDesktop && showHover && (
                                        <Picture
                                            imagexs={productImages[index + 1].src}
                                            position="absolute"
                                            alt={productImages[index + 1].alt}
                                            objectFit="cover"
                                            className="productTile__image-hover-fade invisible"
                                            hasLazy={hasLazy || index > 1}
                                        />
                                    )}

                                    <Picture
                                        imagexs={image.src}
                                        isTileImage
                                        hasLazy={hasLazy || index > 1}
                                        position="absolute"
                                        alt={image.alt}
                                        objectFit="cover"
                                    />
                                </>
                            )}
                        </SwiperSlide>
                    )
                })}
            </Swiper>
            {isDesktop && (
                <Flex
                    as="button"
                    sx={{...styles.navigationNext, visibility: showArrows ? undefined : 'hidden'}}
                    aria-label={formatMessage({id: 'accessibility.button.slide.next.photo', defaultMessage: 'Next photo'})}
                    className={`swiper-button-next swiper-button-next-product-tile--${productId}`}
                    data-testid="swiper-button-next-product-tile"
                    onClick={(event) => handleArrowsClick(event)}
                >
                    <Icon name="IconChevronRight" size="lg" />
                </Flex>
            )}
        </Box>
    )
}
